import Dexie, { type Table } from 'dexie';
import { useLiveQuery } from 'dexie-react-hooks';

export interface SampleData {
  id?: number;
  name: string;
  description: string;
  createdAt: number;
  updatedAt: number;
}

export interface OfflineQueue {
  id?: number;
  type: 'mutation' | 'query';
  endpoint: string;
  method: string;
  body?: unknown;
  headers?: Record<string, string>;
  timestamp: number;
  retries: number;
  status: 'pending' | 'processing' | 'failed' | 'completed';
}

export interface UserPreferences {
  id?: number;
  key: string;
  value: string;
  updatedAt: number;
}

export interface TourProgress {
  id?: number;
  tourId: string;
  userId: string;
  stepIndex: number;
  status: 'in-progress' | 'completed' | 'skipped';
  updatedAt: number;
}

class YesChefDatabase extends Dexie {
  sampleData!: Table<SampleData>;
  offlineQueue!: Table<OfflineQueue>;
  userPreferences!: Table<UserPreferences>;
  tourProgress!: Table<TourProgress>;

  constructor() {
    super('YesChefDB');

    this.version(1).stores({
      sampleData: '++id, name, createdAt, updatedAt',
      offlineQueue: '++id, timestamp, status',
      userPreferences: '++id, key, updatedAt',
    });

    this.version(2).stores({
      sampleData: '++id, name, createdAt, updatedAt',
      offlineQueue: '++id, timestamp, status',
      userPreferences: '++id, key, updatedAt',
      tourProgress: '++id, [tourId+userId], status',
    });
  }
}

export const db = new YesChefDatabase();

// Seed initial data
export async function seedDatabase() {
  const count = await db.sampleData.count();

  if (count === 0) {
    const now = Date.now();
    await db.sampleData.bulkAdd([
      {
        name: 'Sample Item 1',
        description: 'This is a sample item for offline demonstration',
        createdAt: now,
        updatedAt: now,
      },
      {
        name: 'Sample Item 2',
        description: 'Another sample item to show offline capabilities',
        createdAt: now,
        updatedAt: now,
      },
      {
        name: 'Sample Item 3',
        description: 'Third sample item with offline-first architecture',
        createdAt: now,
        updatedAt: now,
      },
    ]);
  }
}

// Initialize database on import
seedDatabase().catch(console.error);

// Helper hooks
export function useSampleData() {
  return useLiveQuery(() => db.sampleData.orderBy('createdAt').reverse().toArray());
}

export function useOfflineQueue() {
  return useLiveQuery(() =>
    db.offlineQueue
      .where('status')
      .anyOf(['pending', 'processing'])
      .sortBy('timestamp')
  );
}

export function useUserPreference(key: string) {
  return useLiveQuery(() =>
    db.userPreferences.where('key').equals(key).first()
  );
}

export async function setUserPreference(key: string, value: string) {
  const existing = await db.userPreferences.where('key').equals(key).first();
  const now = Date.now();

  if (existing) {
    await db.userPreferences.update(existing.id!, { value, updatedAt: now });
  } else {
    await db.userPreferences.add({ key, value, updatedAt: now });
  }
}

export async function getUserPreference(key: string): Promise<string | null> {
  const pref = await db.userPreferences.where('key').equals(key).first();
  return pref?.value ?? null;
}

// Tour progress helpers
export async function saveTourProgress(
  tourId: string,
  userId: string,
  stepIndex: number,
  status: TourProgress['status'],
) {
  const existing = await db.tourProgress.where({ tourId, userId }).first();
  const now = Date.now();
  if (existing) {
    await db.tourProgress.update(existing.id!, { stepIndex, status, updatedAt: now });
  } else {
    await db.tourProgress.add({ tourId, userId, stepIndex, status, updatedAt: now });
  }
}

export async function getTourProgress(tourId: string, userId: string) {
  return db.tourProgress.where({ tourId, userId }).first();
}

export async function isTourCompleted(tourId: string, userId: string): Promise<boolean> {
  const progress = await getTourProgress(tourId, userId);
  return progress?.status === 'completed' || progress?.status === 'skipped';
}
